import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import Button from '@mui/material/Button';
import { useState } from 'react';

import { ButtonLink } from '#components/router-links/button-link.js';
import { CreditRequiredContactGate } from '#src/routes/projects/file-upload/design-month-gate/credit-required-contact-gate.js';
import { RevisionActionKind } from '#src/routes/projects/project-overview/revision-timeline-utils.js';
import { useRevisionAction } from '#src/routes/projects/project-overview/use-revision-action.js';

import type { ProjectTriageItem } from './project-triage-utils.js';

export interface ProjectTriageRowActionProps {
	project: ProjectTriageItem;
	orgName: string;
}

/** The single next-action control for a project row. */
export function ProjectTriageRowAction({ project, orgName }: ProjectTriageRowActionProps) {
	const [contactOpen, setContactOpen] = useState(false);
	const action = useRevisionAction(
		project.status === undefined ?
			null
		:	{
				status: project.status,
				hasUploadedFiles: project.hasUploadedFiles,
				automationMode: project.automationMode,
			},
		orgName,
	);

	if (project.latestRevisionId === undefined || action === null) {
		return null;
	}

	const params = { projectId: project.id, revisionId: project.latestRevisionId };

	switch (action.kind) {
		case RevisionActionKind.Upload: {
			return (
				<ButtonLink
					from='/orgs/$orgName/projects/'
					to='./$projectId/revisions/$revisionId/upload'
					params={params}
					variant='contained'
				>
					<span>{action.label}</span>
				</ButtonLink>
			);
		}
		case RevisionActionKind.BomReview: {
			return (
				<ButtonLink
					from='/orgs/$orgName/projects/'
					to='./$projectId/revisions/$revisionId/results'
					params={params}
					search={{ leftTab: 'bom' }}
					variant='contained'
					endIcon={<ArrowForwardRoundedIcon />}
				>
					<span>{action.label}</span>
				</ButtonLink>
			);
		}
		case RevisionActionKind.StartAnalysis: {
			return (
				<ButtonLink
					from='/orgs/$orgName/projects/'
					to='./$projectId/revisions/$revisionId/results'
					params={params}
					variant='contained'
					endIcon={<ArrowForwardRoundedIcon />}
				>
					<span>{action.label}</span>
				</ButtonLink>
			);
		}
		case RevisionActionKind.AddCredit: {
			return (
				<ButtonLink
					to='/orgs/$orgName/billing/plans'
					params={{ orgName }}
					search={{
						step: 'plans',
						returnTo: `/orgs/${orgName}/projects/${project.id}/revisions/${project.latestRevisionId}/results`,
						projectId: project.id,
						buildId: project.latestRevisionId,
					}}
					variant='contained'
					endIcon={<ArrowForwardRoundedIcon />}
				>
					<span>{action.label}</span>
				</ButtonLink>
			);
		}
		case RevisionActionKind.ContactSales: {
			return (
				<>
					<Button variant='contained' onClick={() => setContactOpen(true)}>
						<span>{action.label}</span>
					</Button>
					<CreditRequiredContactGate
						open={contactOpen}
						orgName={orgName}
						revisionId={project.latestRevisionId}
						onClose={() => setContactOpen(false)}
					/>
				</>
			);
		}
		case RevisionActionKind.ViewError: {
			// The overview carries the failure message itself, so it answers "what
			// went wrong" without a second hop.
			return (
				<ButtonLink
					from='/orgs/$orgName/projects/'
					to='./$projectId/overview'
					params={{ projectId: project.id }}
					variant='outlined'
					color='error'
				>
					<span>{action.label}</span>
				</ButtonLink>
			);
		}
		case RevisionActionKind.ViewProgress:
		case RevisionActionKind.ViewResults: {
			return (
				<ButtonLink
					from='/orgs/$orgName/projects/'
					to='./$projectId/revisions/$revisionId/results'
					params={params}
					variant='outlined'
				>
					<span>{action.label}</span>
				</ButtonLink>
			);
		}
	}
}
